import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Clock, User, PenSquare, RefreshCw } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useFollows } from '../hooks/useFollows';
import { timeAgo } from '../lib/utils';
import VerifiedBadge from '../components/VerifiedBadge';
import { isBCHContent } from '../lib/bchFilter';
import { BCHLogo } from '../components/Layout';
import { INK_NAME, INK_MARK, INK_TAGLINE, inkArticlePath, inkBaseFor } from '../lib/ink';

const ARTICLES_PER_PAGE = 20;

// Known BCH community authors whose articles always show
const BCH_AUTHORS = [
  'df5bcaba9e74cc1764c9773ae160299dde5acf79416e2c96758dba225e6707cc', // BCH Reply Guy
];

export default function BlogPage() {
  const { user } = useAppContext();
  const location = useLocation();
  const base = inkBaseFor(location.pathname);
  const { follows } = useFollows(user?.pubkey);
  const [tab, setTab] = useState<'latest' | 'following' | 'popular'>('latest');

  const [since] = useState(() => Math.floor(Date.now() / 1000) - 90 * 86400); // last 90 days

  // Build robust author list — always includes self + known BCH authors
  const knownAuthors = useMemo(() => {
    const set = new Set(BCH_AUTHORS);
    if (user) set.add(user.pubkey);
    follows.forEach(f => set.add(f));
    return [...set];
  }, [user, follows]);

  // Fetch articles — kind 30023 (NIP-23 long-form). Unchanged discovery
  // strategy: tag-based + author-based + own articles, merged & de-duped.
  const { events: allArticles, loading, refetch } = useNostrQuery({
    filters: [
      // Tag-based discovery
      { kinds: [30023], '#t': ['bch'], limit: 100, since },
      { kinds: [30023], '#t': ['bitcoincash'], limit: 100, since },
      { kinds: [30023], '#t': ['bchnostr'], limit: 50, since },
      // Author-based — always fetch from known authors, follows, and self
      ...(knownAuthors.length > 0 ? [{ kinds: [30023], authors: knownAuthors, limit: 200, since }] : []),
      // Own articles with no time limit
      ...(user ? [{ kinds: [30023], authors: [user.pubkey], limit: 50 }] : []),
    ],
    enabled: true,
    allRelays: true,
  });

  // Fetch user's deletion events (kind 5) to hide deleted articles
  const { events: deletionEvents } = useNostrQuery({
    filters: user ? [{ kinds: [5], authors: [user.pubkey], '#k': ['30023'], limit: 100 }] : [],
    enabled: !!user,
  });

  // Build deletion lookup: event IDs are always deleted, but a-tag (coordinate)
  // deletions only apply to articles created BEFORE the deletion event (per NIP-09)
  const deletionInfo = useMemo(() => {
    const deletedEventIds = new Set<string>();
    const deletedCoords = new Map<string, number>();
    deletionEvents.forEach(ev => {
      ev.tags.forEach((t: string[]) => {
        if (t[0] === 'e') deletedEventIds.add(t[1]);
        if (t[0] === 'a') {
          const existing = deletedCoords.get(t[1]);
          if (!existing || ev.created_at > existing) {
            deletedCoords.set(t[1], ev.created_at);
          }
        }
      });
    });
    return { deletedEventIds, deletedCoords };
  }, [deletionEvents]);

  // Deduplicate by d-tag (keep latest version of each article)
  // Filter out non-article events and deleted articles
  const articles = useMemo(() => {
    const byDTag = new Map<string, any>();
    allArticles.forEach(ev => {
      const title = ev.tags.find((t: string[]) => t[0] === 'title')?.[1];
      if (!title || !title.trim()) return;

      const trimmed = ev.content.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) return;

      const isOwn = user && ev.pubkey === user.pubkey;
      if (!isOwn && !isBCHContent(ev.content, ev.tags)) return;

      const dTag = ev.tags.find((t: string[]) => t[0] === 'd')?.[1];

      if (deletionInfo.deletedEventIds.has(ev.id)) return;
      if (dTag) {
        const coord = `30023:${ev.pubkey}:${dTag}`;
        const deletedAt = deletionInfo.deletedCoords.get(coord);
        if (deletedAt && ev.created_at <= deletedAt) return;
      }

      const key = dTag ? `${ev.pubkey}:${dTag}` : ev.id;
      const existing = byDTag.get(key);
      if (!existing || ev.created_at > existing.created_at) {
        byDTag.set(key, ev);
      }
    });
    return [...byDTag.values()].sort((a, b) => b.created_at - a.created_at);
  }, [allArticles, user, deletionInfo]);

  // Tip receipts across all articles — used to rank "Popular"
  const allArticleIds = useMemo(() => articles.map(a => a.id), [articles]);
  const { events: tipReceipts } = useNostrQuery({
    filters: allArticleIds.length > 0 ? [{ kinds: [9735], '#e': allArticleIds, '#t': ['bch-tip'], limit: 500 }] : [],
    enabled: allArticleIds.length > 0,
  });

  const tipsByArticle = useMemo(() => {
    const map = new Map<string, number>();
    tipReceipts.forEach(ev => {
      const eTag = ev.tags.find((t: string[]) => t[0] === 'e')?.[1];
      if (!eTag) return;
      const amountTag = ev.tags.find((t: string[]) => t[0] === 'amount')?.[1];
      let sats = 0;
      if (amountTag) sats = parseInt(amountTag, 10) || 0;
      else {
        try { sats = JSON.parse(ev.content).amount_sats || 0; } catch {}
      }
      if (sats > 0) map.set(eTag, (map.get(eTag) || 0) + sats);
    });
    return map;
  }, [tipReceipts]);

  const filtered = useMemo(() => {
    if (tab === 'following') {
      // Only articles from people the reader follows (falls back to empty,
      // which surfaces a friendly "follow writers" prompt below).
      return follows.length > 0 ? articles.filter(a => follows.includes(a.pubkey)) : [];
    }
    if (tab === 'popular') {
      // Rank by total sats tipped, then recency. Non-destructive: same events,
      // just sorted differently.
      return [...articles].sort((a, b) => {
        const ta = tipsByArticle.get(a.id) || 0;
        const tb = tipsByArticle.get(b.id) || 0;
        if (tb !== ta) return tb - ta;
        return b.created_at - a.created_at;
      });
    }
    return articles; // latest
  }, [articles, tab, follows, tipsByArticle]);

  const [visibleCount, setVisibleCount] = useState(ARTICLES_PER_PAGE);
  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="flex items-start justify-between gap-3 px-4 py-3.5">
          <div className="min-w-0">
            <h1 className="text-xl font-bold flex items-center gap-1.5 leading-none">
              <span aria-hidden>{INK_MARK}</span> {INK_NAME}
            </h1>
            <p className="text-xs text-muted-foreground mt-1">{INK_TAGLINE}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={refetch} className="p-2 rounded-lg hover:bg-muted text-muted-foreground" title="Refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {user && (
              <Link
                to={`${base}/write`}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 shadow-sm"
              >
                <PenSquare className="w-4 h-4" /> Write
              </Link>
            )}
          </div>
        </div>

        {/* Nav — Latest / Following / Popular */}
        <div className="flex border-t border-border">
          {[
            { id: 'latest' as const, label: 'Latest' },
            { id: 'following' as const, label: 'Following' },
            { id: 'popular' as const, label: 'Popular' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); setVisibleCount(ARTICLES_PER_PAGE); }}
              className={`flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Articles */}
      <div className="p-4 max-w-2xl mx-auto">
        {loading && visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <BCHLogo size={40} />
            <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Loading articles…</p>
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-4xl mb-3" aria-hidden>{INK_MARK}</div>
            <p className="text-muted-foreground text-sm">
              {tab === 'following'
                ? (follows.length === 0
                    ? 'Follow some writers and their articles will appear here.'
                    : 'No articles yet from people you follow.')
                : `No articles on ${INK_NAME} yet.`}
            </p>
            {user && (
              <Link to={`${base}/write`} className="inline-flex items-center gap-1.5 mt-3 text-sm text-primary hover:underline">
                <PenSquare className="w-4 h-4" /> Write the first one
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-3.5">
            {visible.map(article => (
              <ArticleCard
                key={article.id}
                event={article}
                base={base}
              />
            ))}
            {hasMore && (
              <div className="text-center py-4">
                <button
                  onClick={() => setVisibleCount(c => c + ARTICLES_PER_PAGE)}
                  className="px-6 py-2.5 rounded-full border border-border text-sm font-medium hover:bg-muted"
                >
                  Load more
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Article preview card — clean, reading-focused ─────────────────
function ArticleCard({ event, base }: { event: any; base: string }) {
  const { profile } = useNostrProfile(event.pubkey);

  const title = event.tags.find((t: string[]) => t[0] === 'title')?.[1] || 'Untitled';
  const summary = event.tags.find((t: string[]) => t[0] === 'summary')?.[1]
    || event.content.slice(0, 200).replace(/[#*_[\]`>]/g, '').trim() + '…';
  const image = event.tags.find((t: string[]) => t[0] === 'image')?.[1];
  const publishedAt = event.tags.find((t: string[]) => t[0] === 'published_at')?.[1];
  const tags = event.tags.filter((t: string[]) => t[0] === 't').map((t: string[]) => t[1]);
  const dTag = event.tags.find((t: string[]) => t[0] === 'd')?.[1] || event.id;
  const displayName = profile?.display_name || profile?.name || event.pubkey.slice(0, 12) + '…';

  const naddr = nip19.naddrEncode({ identifier: dTag, pubkey: event.pubkey, kind: 30023 });

  const wordCount = event.content.split(/\s+/).length;
  const readingTime = Math.max(1, Math.round(wordCount / 200));

  return (
    <Link
      to={inkArticlePath(naddr, base)}
      className="block rounded-2xl border border-border bg-card hover:border-primary/30 hover:shadow-sm transition-all overflow-hidden"
    >
      {image && (
        <div className="aspect-[2.4/1] overflow-hidden bg-muted">
          <img
            src={image}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
          />
        </div>
      )}

      <div className="p-4 sm:p-5 space-y-3">
        <h2 className="text-lg sm:text-xl font-bold leading-snug line-clamp-2">{title}</h2>

        <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">{summary}</p>

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {tags.slice(0, 4).map((tag: string) => (
              <span key={tag} className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium">#{tag}</span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-2 min-w-0">
            {profile?.picture ? (
              <img src={profile.picture} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" loading="lazy" />
            ) : (
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <User className="w-3 h-3 text-primary/60" />
              </div>
            )}
            <span className="text-xs font-medium truncate">{displayName}</span>
            <VerifiedBadge nip05={profile?.nip05} pubkey={event.pubkey} size="xs" />
          </div>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground shrink-0">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{readingTime} min</span>
            <span>·</span>
            <span>{timeAgo(publishedAt ? parseInt(publishedAt) : event.created_at)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
